import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Trash2,
  Minus,
  Plus,
  ShoppingBag,
  ArrowLeft,
  Loader2,
  Tag,
  Check,
  X,
  Truck,
} from 'lucide-react';
import {
  getCart,
  updateCartItem,
  removeFromCart,
  createCheckoutSession,
  validatePromoCode,
} from '../api';
import { useToast } from '../hooks/use-toast';

const FREE_SHIPPING_THRESHOLD = 100;
const SHIPPING_FEE = 10;

export const Cart = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cart, setCart] = useState([]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_amount, message }
  const [validatingPromo, setValidatingPromo] = useState(false);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    setCart(getCart());
  };

  const handleUpdateQuantity = (cartId, newQuantity) => {
    if (newQuantity < 1) return;
    updateCartItem(cartId, newQuantity);
    loadCart();
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleRemove = (cartId) => {
    removeFromCart(cartId);
    loadCart();
    window.dispatchEvent(new Event('cartUpdated'));
    toast({ title: 'Item removed', description: 'Item removed from cart' });
  };

  const handleApplyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;

    setValidatingPromo(true);
    try {
      const result = await validatePromoCode(code, subtotal);
      if (result.valid) {
        setAppliedPromo(result);
        toast({ title: 'Code applied! 🎉', description: result.message });
      } else {
        setAppliedPromo(null);
        toast({
          title: 'Invalid code',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Could not validate code',
        variant: 'destructive',
      });
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast({
        title: 'Cart is empty',
        description: 'Add items to cart before checkout',
        variant: 'destructive',
      });
      return;
    }

    setIsCheckingOut(true);

    try {
      const originUrl = window.location.origin;
      const response = await createCheckoutSession(
        cart,
        originUrl,
        customerEmail || null,
        appliedPromo?.code || null
      );

      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      const errMessage =
        err.response?.data?.detail || 'Failed to start checkout. Try again.';
      toast({
        title: 'Checkout Error',
        description: errMessage,
        variant: 'destructive',
      });
      setIsCheckingOut(false);
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const freeShippingQualified = subtotal >= FREE_SHIPPING_THRESHOLD;
  const shipping = subtotal > 0 ? (freeShippingQualified ? 0 : SHIPPING_FEE) : 0;
  const discount = appliedPromo?.discount_amount || 0;
  const total = Math.max(subtotal + shipping - discount, 0);
  const amountToFreeShipping = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-[8%]">
        <div className="max-w-2xl mx-auto text-center" data-testid="empty-cart">
          <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-gray-600" />
          <h1 className="text-4xl font-bold text-white mb-4">Your Cart is Empty</h1>
          <p className="text-gray-400 mb-8">Start adding some fresh streetwear!</p>
          <Button
            onClick={() => navigate('/')}
            data-testid="continue-shopping-btn"
            className="bg-[#ff1f4d] hover:bg-white hover:text-black font-bold"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-[8%]">
      <div className="max-w-6xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-white hover:text-[#ff1f4d]"
          data-testid="back-to-shop-btn"
        >
          <ArrowLeft className="mr-2" /> Continue Shopping
        </Button>

        <h1 className="text-5xl font-black text-[#ff1f4d] mb-10">Shopping Cart</h1>

        {/* Free shipping progress */}
        {!freeShippingQualified && cart.length > 0 && (
          <Card className="bg-[#ff1f4d]/10 border-[#ff1f4d]/30 p-4 mb-6 flex items-center gap-3" data-testid="free-shipping-banner">
            <Truck className="h-6 w-6 text-[#ff1f4d] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-white font-bold text-sm">
                Add <span className="text-[#ff1f4d]">${amountToFreeShipping.toFixed(2)}</span> more for FREE SHIPPING
              </p>
              <div className="mt-2 h-2 bg-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#ff1f4d] transition-all duration-500"
                  style={{ width: `${Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                />
              </div>
            </div>
          </Card>
        )}
        {freeShippingQualified && (
          <Card className="bg-green-900/20 border-green-700/50 p-4 mb-6 flex items-center gap-3" data-testid="free-shipping-unlocked">
            <Check className="h-6 w-6 text-green-400 flex-shrink-0" />
            <p className="text-white font-bold">You unlocked FREE SHIPPING! 🎉</p>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map(item => (
              <Card key={item.cartId} className="bg-[#111] border-[#222] p-4" data-testid={`cart-item-${item.cartId}`}>
                <div className="flex gap-4">
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover rounded" />
                  <div className="flex-1">
                    <h3 className="text-white text-lg font-semibold">{item.name}</h3>
                    <p className="text-gray-400 text-sm">Size: {item.size}</p>
                    <p className="text-[#ff1f4d] font-bold mt-1">${item.price}</p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <Button onClick={() => handleRemove(item.cartId)} variant="ghost" size="icon" className="text-gray-400 hover:text-red-500" data-testid={`remove-item-${item.cartId}`}>
                      <Trash2 className="h-5 w-5" />
                    </Button>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => handleUpdateQuantity(item.cartId, item.quantity - 1)} variant="outline" size="icon" className="h-8 w-8 bg-black border-[#222] text-white hover:bg-[#222]">
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="text-white w-8 text-center">{item.quantity}</span>
                      <Button onClick={() => handleUpdateQuantity(item.cartId, item.quantity + 1)} variant="outline" size="icon" className="h-8 w-8 bg-black border-[#222] text-white hover:bg-[#222]">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="bg-[#111] border-[#222] p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>

              {/* Promo code section */}
              <div className="mb-6">
                {!appliedPromo ? (
                  <div>
                    <Label className="text-gray-300 text-sm flex items-center gap-1 mb-2">
                      <Tag className="h-3 w-3" /> Promo Code
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="STAYTURNT10"
                        className="bg-black border-[#222] text-white"
                        data-testid="promo-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                      />
                      <Button
                        onClick={handleApplyPromo}
                        disabled={!promoInput.trim() || validatingPromo}
                        className="bg-[#ff1f4d] hover:bg-white hover:text-black font-bold disabled:opacity-50"
                        data-testid="apply-promo-btn"
                      >
                        {validatingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-3 flex items-center justify-between" data-testid="applied-promo">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-400" />
                      <div>
                        <p className="text-white font-bold">{appliedPromo.code}</p>
                        <p className="text-gray-400 text-xs">{appliedPromo.message}</p>
                      </div>
                    </div>
                    <Button onClick={handleRemovePromo} variant="ghost" size="icon" className="text-gray-400 hover:text-red-400 h-8 w-8" data-testid="remove-promo-btn">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6 border-t border-[#222] pt-4">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-400" data-testid="cart-discount">
                    <span>Discount</span>
                    <span>−${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-400">
                  <span>Shipping</span>
                  <span>
                    {shipping === 0 ? (
                      <span className="text-green-400">FREE</span>
                    ) : (
                      `$${shipping.toFixed(2)}`
                    )}
                  </span>
                </div>
                <div className="border-t border-[#222] pt-3 flex justify-between text-white text-xl font-bold">
                  <span>Total</span>
                  <span className="text-[#ff1f4d]" data-testid="cart-total">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="mb-4">
                <Label htmlFor="email" className="text-gray-300 text-sm">
                  Email (optional)
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="bg-black border-[#222] text-white mt-1"
                  data-testid="customer-email-input"
                />
              </div>

              <Button
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className="w-full bg-[#ff1f4d] hover:bg-white hover:text-black font-bold py-6 text-lg disabled:opacity-50"
                data-testid="checkout-btn"
              >
                {isCheckingOut ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  'Proceed to Checkout'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-3">
                Secure payment powered by Stripe
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
